import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleAdaptiveNavbar from '../../components/ui/RoleAdaptiveNavbar';
import GalleryNavigationBreadcrumb from '../../components/ui/GalleryNavigationBreadcrumb';
import StepIndicator from './components/StepIndicator';
import BasicInfoStep from './components/BasicInfoStep';
import GoogleDriveStep from './components/GoogleDriveStep';
import PrivacySettingsStep from './components/PrivacySettingsStep';
import ReviewStep from './components/ReviewStep';
import Button from '../../components/ui/Button';
import { galleryService } from '../../services/galleryService';
import { mediaService } from '../../services/mediaService';


const GalleryCreation = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);

  const [formData, setFormData] = useState({
    galleryName: '',
    description: '',
    galleryType: '',
    template: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    selectedFolders: {
      highRes: '',
      highResName: '',
      compressed: '',
      compressedName: ''
    },
    accessLevel: 'password',
    downloadPermission: 'selected',
    requireRegistration: true,
    trackActivity: true,
    enableAnalytics: true,
    sendNotifications: false,
    enableComments: false,
    enableFavorites: true,
    autoSync: true,
    enableWatermark: false
  });

  const [errors, setErrors] = useState({});

  const steps = [
    { id: 1, label: 'Basic Info' },
    { id: 2, label: 'Google Drive' },
    { id: 3, label: 'Privacy' },
    { id: 4, label: 'Review' }
  ];

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/contributor-dashboard', icon: 'LayoutDashboard' },
    { label: 'Create Gallery', icon: 'Plus' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.galleryName?.trim()) {
        newErrors.galleryName = 'Gallery name is required';
      }
      if (!formData?.galleryType) {
        newErrors.galleryType = 'Please select a gallery type';
      }
      if (!formData?.template) {
        newErrors.template = 'Please select a layout template';
      }
      if (!formData?.clientName?.trim()) {
        newErrors.clientName = 'Client name is required';
      }
      if (!formData?.clientEmail?.trim()) {
        newErrors.clientEmail = 'Client email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.clientEmail)) {
        newErrors.clientEmail = 'Please enter a valid email address';
      }
    }

    if (step === 2) {
      if (!formData?.selectedFolders?.highRes || !formData?.selectedFolders?.compressed) {
        newErrors.selectedFolders = 'Please select both high-resolution and compressed folders';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps?.length));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleEditStep = (stepNumber) => {
    setCurrentStep(stepNumber);
  };

  const handleCreateGallery = async () => {
    if (validateStep(1) && validateStep(2)) {
      setIsCreating(true);
      try {
        // Create gallery with Google Drive folder IDs
        const galleryData = {
          galleryName: formData?.galleryName,
          description: formData?.description,
          galleryType: formData?.galleryType,
          template: formData?.template,
          clientName: formData?.clientName,
          clientEmail: formData?.clientEmail,
          clientPhone: formData?.clientPhone,
          requireMediaMetadata: formData?.requireRegistration,
          downloadEnabled: formData?.downloadPermission !== 'none',
          isPublished: false,
          driveHighResFolderId: formData?.selectedFolders?.highRes,
          driveHighResFolderName: formData?.selectedFolders?.highResName,
          driveCompressedFolderId: formData?.selectedFolders?.compressed,
          driveCompressedFolderName: formData?.selectedFolders?.compressedName,
          autoSync: formData?.autoSync
        };

        const gallery = await galleryService?.createGallery(galleryData);

        // If auto-sync is enabled, sync media from compressed folder
        if (formData?.autoSync && formData?.selectedFolders?.compressed) {
          try {
            await mediaService?.syncFolderMedia(
              gallery?.id,
              formData?.selectedFolders?.compressed,
              'compressed'
            );
          } catch (syncError) {
            console.error('Initial sync failed:', syncError);
            // Continue even if sync fails - user can manually sync later
          }
        }

        // Navigate to gallery management
        navigate(`/gallery-management?id=${gallery?.id}`);
      } catch (error) {
        console.error('Error creating gallery:', error);
        alert('Failed to create gallery. Please try again.');
      } finally {
        setIsCreating(false);
      }
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoStep
            formData={formData}
            onChange={handleChange}
            errors={errors}
          />
        );
      case 2:
        return (
          <GoogleDriveStep
            formData={formData}
            onChange={handleChange}
            errors={errors}
          />
        );
      case 3:
        return (
          <PrivacySettingsStep
            formData={formData}
            onChange={handleChange}
          />
        );
      case 4:
        return (
          <ReviewStep
            formData={formData}
            onEdit={handleEditStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleAdaptiveNavbar userRole="contributor" userName="John Photographer" />
      <div className="pt-16 md:pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <GalleryNavigationBreadcrumb items={breadcrumbItems} userRole="contributor" />

          <div className="mt-4 md:mt-6 mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold mb-2">Create New Gallery</h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Set up a new client gallery with Google Drive integration and custom settings
            </p>
          </div>

          <StepIndicator currentStep={currentStep} steps={steps} />

          <div className="mb-6 md:mb-8">
            {renderStepContent()}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4 pb-8">
            <Button
              variant="outline"
              size="lg"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="w-full sm:w-auto"
            >
              Back
            </Button>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="data-text">Step {currentStep} of {steps?.length}</span>
            </div>

            {currentStep < steps?.length ? (
              <Button
                variant="default"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={handleNext}
                className="w-full sm:w-auto"
              >
                Next Step
              </Button>
            ) : (
              <Button
                variant="default"
                size="lg"
                iconName="CheckCircle2"
                iconPosition="left"
                onClick={handleCreateGallery}
                loading={isCreating}
                className="w-full sm:w-auto"
              >
                Create Gallery
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryCreation;